import { getOrFetchLookup, lookupCacheKeys } from '@/utils/lookupCache';
import { api, unwrap, repeatedParams } from './http';

export default {
  list: async (params = {}, config = {}) => unwrap(api.get('/Memberships', params, config)),

  getById: async (id) => unwrap(api.get(`/Memberships/${id}`)),

  create: async (payload) => unwrap(api.post('/Memberships', payload)),

  update: async (id, payload) => unwrap(api.put(`/Memberships/${id}`, payload)),

  remove: async (id) => unwrap(api.delete(`/Memberships/${id}`)),

  // The ceilings the cancellation form validates against, read from the membership's own ledger so
  // the form cannot offer an amount the save would refuse.
  getCancellationPreview: async (id) => unwrap(api.get(`/Memberships/${id}/Cancellation/Preview`)),

  cancel: async (id, payload) => unwrap(api.post(`/Memberships/${id}/Cancel`, payload)),

  bulkDelete: async (ids) => unwrap(api.delete('/Memberships', repeatedParams('ids', ids))),

  bulkChangeStatus: async (membershipIds, statusCode, noteMessage) => {
    const params = repeatedParams('MembershipIds', membershipIds);
    params.append('StatusCode', statusCode);
    if (noteMessage !== undefined) params.append('NoteMessage', noteMessage);
    return unwrap(api.put(`/Memberships/MembershipBulkStatusChange?${params.toString()}`));
  },

  bulkAddEntries: async (membershipIds, numberOfEntries, noteMessage) => {
    const params = repeatedParams('MembershipIds', membershipIds);
    params.append('NumberOfEntries', numberOfEntries);
    if (noteMessage !== undefined) params.append('NoteMessage', noteMessage);
    return unwrap(api.put(`/Memberships/AddEntries?${params.toString()}`));
  },

  getExtending: async (params = {}) => unwrap(api.get('/Memberships/ExtendingMemberships', params)),

  extend: async (payload) => unwrap(api.put('/Memberships/ExtendingMemberships', payload)),

  getMembershipPeriod: async (payload) => unwrap(api.post('/Memberships/GetMembershipPeriod', payload)),

  getNumberOfActiveToday: async () => unwrap(api.get('/Memberships/GetNumberofMembershipActiveToday')),

  // config carries the report's own branch scope; see utils/branchScope.
  getMembersCountReport: async (params = {}, config = {}) => unwrap(
    api.get('/Memberships/Members/Count/Report', params, config),
  ),

  // The members behind one figure of that report. Same branch scope as the figure it was opened
  // from, or the count and the list would be answering two different questions.
  getMembersCountReportMembers: async (params = {}, config = {}) => unwrap(
    api.get('/Memberships/Members/Count/Report/Members', params, config),
  ),

  getPackageMemberLimit: async (params = {}) => unwrap(api.get('/Memberships/Package/Member/Limit', params)),

  printEnrollment: async (params = {}) => unwrap(api.get('/Memberships/PrintMembershipEnrollment', params)),

  // MembershipType endpoints
  // `branchId` scopes the list to one branch for this request only, without moving the global
  // switcher: selling a membership asks for the plans offered at the branch the sale is booked
  // against, which is the form's own field and not whatever branch the operator is viewing.
  getMembershipTypes: async ({
    statusCode = '', orderBy = 'StatusDescription', orderDir = 'asc', branchId, ...rest
  } = {}) => (
    unwrap(api.get('/MembershipType', {
      statusCode, orderBy, orderDir, ...rest,
    }, branchId ? { headers: { 'X-Branch-Id': String(branchId) } } : {}))
  ),

  getMembershipTypeById: async (id) => unwrap(api.get(`/MembershipType/${id}`)),

  createMembershipType: async (payload) => unwrap(api.post('/MembershipType', payload)),

  updateMembershipType: async (id, payload) => unwrap(api.put(`/MembershipType/${id}`, payload)),

  removeMembershipType: async (id) => unwrap(api.delete(`/MembershipType/${id}`)),

  bulkDeleteMembershipTypes: async (ids) => unwrap(api.delete('/MembershipType', repeatedParams('ids', ids))),

  getMembershipTypeChart: async (params = {}) => unwrap(api.get('/MembershipType/Chart', params)),

  getMembershipTypeClasses: async (params = {}) => unwrap(api.get('/MembershipType/Classes', params)),

  // FITNET-107: plan-level allowed activities (rule engine); [] = all activities allowed.
  setPlanActivities: async (membershipTypeId, activityIds) => unwrap(api.put(`/MembershipType/${membershipTypeId}/Activities`, activityIds)),

  // Statuses lookup (used by membership forms)
  getStatuses: async (params = {}, options = {}) => getOrFetchLookup({
    key: lookupCacheKeys.statuses(params),
    fetcher: () => unwrap(api.get('/Statuses', params)),
    ttlMs: options.ttlMs,
    forceRefresh: options.forceRefresh,
  }),
};
